// src/components/ActionItemListView.js
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import DetailedListView from './DetailedListView';
import { actionItemQueries, initiativeQueries, updateQueries, staffQueries } from '../repository/graphQueries'; 
import { formatDate, formatScore } from '../utils'; 
import { ACTION_ITEM_STATUS_OPTIONS, DRI_EXPLANATION } from '../constants';
import { RiInformationLine as InformationCircleIcon } from 'react-icons/ri';

const DropdownCheckbox = ({ options, selectedOptions, onChange, title }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
        <div className="relative inline-block text-left">
            <div>
                <button type="button" className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none" onClick={() => setIsOpen(!isOpen)}>
                    {title}
                    <svg className="-mr-1 ml-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                </button>
            </div>
            {isOpen && (
                <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                    <div className="py-1" role="menu" aria-orientation="vertical">
                        {options.map(option => (
                            <label key={option} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                                <input type="checkbox" className="form-checkbox h-4 w-4 text-indigo-600 transition duration-150 ease-in-out" checked={selectedOptions.includes(option)} onChange={() => onChange(option)} />
                                <span className="ml-3">{option}</span>
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

const Tooltip = ({ content, children }) => {
    const [isVisible, setIsVisible] = useState(false);
    return (
        <div className="relative inline-block">
            <div className="inline-flex items-center cursor-help" onMouseEnter={() => setIsVisible(true)} onMouseLeave={() => setIsVisible(false)} onClick={() => setIsVisible(!isVisible)}>
                {children}
            </div>
            {isVisible && (
                <div className="absolute top-full right-0 mt-2 w-72 p-3 text-sm bg-gray-800 text-white rounded-md shadow-lg z-50">
                    {content}
                    <div className="absolute bottom-full right-2 w-0 h-0 border-x-4 border-x-transparent border-b-4 border-b-gray-800"></div>
                </div>
            )}
        </div>
    );
};

const cleanDriList = (rawDriList) => {
    if (!rawDriList) return [];
    const separators = / and | & | \/ | \\ | with | for |,| featuring | assisting | cc /i;
    const allNames = rawDriList.flatMap(dri => {
        if (typeof dri !== 'string') return [];
        const cleaned = dri.replace(/\(.*\)|\[.*\]/g, '');
        return cleaned.split(separators);
    });
    const finalDris = allNames
        .map(name => name.trim())
        .filter(name => name && name.length > 1);
    return [...new Set(finalDris)].sort();
};

const actionItemColumns = [
    { header: 'Name', key: 'name' },
    { header: 'Assignee', key: 'assignee'},
    { header: 'Deadline', key: 'deadline', formatter: formatDate },
    { header: 'Initiative', key: 'initiativeName'},
    {
        header: 'Urgency Score',
        key: 'score',
        align: 'center',
        formatter: formatScore
    },
    { header: 'Status', key: 'status' },
    { header: 'Description', key: 'description', type: 'longText' }
];

const ActionItemListView = () => {
    const { neo4jService, clientId } = useOutletContext();

    const [pendingActionItemsList, setPendingActionItemsList] = useState([]);
    const [loadingActionItems, setLoadingActionItems] = useState(false);
    const [errorActionItems, setErrorActionItems] = useState(null);

    const [actionItemsByAssignee, setActionItemsByAssignee] = useState([]);
    const [loadingAssigneeChart, setLoadingAssigneeChart] = useState(false);
    
    const [dris, setDris] = useState([]);
    const [loadingDris, setLoadingDris] = useState(false);
    const [selectedDris, setSelectedDris] = useState([]);

    const [actionItemInitiativeFilter, setActionItemInitiativeFilter] = useState('');
    const [initiativeListForFilter, setInitiativeListForFilter] = useState([]);
    const [loadingInitiativesForFilter, setLoadingInitiativesForFilter] = useState(false);

    const driTooltipContent = DRI_EXPLANATION;

    const loadFilterAndDriOptions = useCallback(async () => {
        if (!neo4jService || !clientId) return;
        setLoadingDris(true); setLoadingInitiativesForFilter(true);
        setDris([]); setInitiativeListForFilter([]);
        try {
            const [driResults, initiativeResults] = await Promise.all([
                 staffQueries.getDistinctDRIsForClient ? neo4jService.runQuery(staffQueries.getDistinctDRIsForClient, { clientId }) : Promise.resolve([]),
                 neo4jService.runQuery(initiativeQueries.getDistinctInitiatives, { clientId })
            ]);
            const rawDriList = driResults.map(r => r.dri);
            setDris(cleanDriList(rawDriList));
            setInitiativeListForFilter(initiativeResults.map(r => r.initiativeName).filter(Boolean));
        } catch (err) { console.error("Error loading filter options:", err); }
        finally { setLoadingDris(false); setLoadingInitiativesForFilter(false); }
    }, [neo4jService, clientId]);

     const loadAssigneeChartData = useCallback(async () => {
         if (!neo4jService || !clientId) return;
         setLoadingAssigneeChart(true);
         try {
             const assigneeData = await neo4jService.runQuery(actionItemQueries.getActionItemsByAssignee, { clientId });
             setActionItemsByAssignee(assigneeData);
         } catch (err) { console.error("Error loading assignee chart data:", err); setActionItemsByAssignee([]); }
         finally { setLoadingAssigneeChart(false); }
     }, [neo4jService, clientId]);

    useEffect(() => {
        if (clientId) {
            loadFilterAndDriOptions();
            loadAssigneeChartData();
        }
    }, [clientId, loadFilterAndDriOptions, loadAssigneeChartData]);

    useEffect(() => {
        const loadPendingActionItemsList = async () => {
            if (!neo4jService || !clientId) return;
            setLoadingActionItems(true); setErrorActionItems(null);
            try {
                const params = {
                    clientId,
                    driFilter: selectedDris.length > 0 ? selectedDris : null,
                    initiativeFilter: actionItemInitiativeFilter || null
                };
                const items = await neo4jService.runQuery(actionItemQueries.getAllPendingActionItemsList, params);
                setPendingActionItemsList(items);
            } catch (err) { setErrorActionItems(err.message); setPendingActionItemsList([]); }
            finally { setLoadingActionItems(false); }
        };
        
        loadPendingActionItemsList();
    }, [neo4jService, clientId, selectedDris, actionItemInitiativeFilter]);

    const handleDriChange = (dri) => {
        setSelectedDris(prev =>
            prev.includes(dri) ? prev.filter(d => d !== dri) : [...prev, dri]
        );
    };

    const handleInitiativeFilterChange = (event) => setActionItemInitiativeFilter(event.target.value);

    const handleActionItemUpdate = (itemId, updatedField, newValue) => {
        // This function is passed to DetailedListView, but the full reload is handled by the useEffect above
        console.log(`Action Item ${itemId} field ${updatedField} updated to ${newValue}. Refreshing list.`);
    };

    const actionItemEditableColumnsConfig = {
        status: {
            options: ACTION_ITEM_STATUS_OPTIONS,
            query: updateQueries.updateNodeStatus,
            propertyName: 'status'
        }
    };

    return (
        <>
            <h2 className="text-xl font-semibold mb-6">Action Items</h2>

            <div className="bg-white rounded-lg shadow p-4 md:p-6 mb-6 md:mb-8">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Pending Items by Assignee</h3>
                {loadingAssigneeChart ? <p>Loading chart...</p> : actionItemsByAssignee.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={actionItemsByAssignee} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis type="number" allowDecimals={false} tick={{fontSize: 10}} />
                            <YAxis dataKey="assignee" type="category" width={120} tick={{fontSize: 11}} interval={0} />
                            <RechartsTooltip wrapperClassName="text-xs" />
                            <Bar dataKey="count" fill="#60A5FA" barSize={20} name="Pending Items" />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <p className="text-center text-sm text-gray-500 py-4">No pending action items found to display chart.</p>
                )}
             </div>

            <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200 grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-center space-x-2">
                     <label className="block text-sm font-medium text-gray-700">DRI:</label>
                     <DropdownCheckbox
                        options={dris}
                        selectedOptions={selectedDris}
                        onChange={handleDriChange}
                        title="Filter by DRI"
                    />
                    <Tooltip content={driTooltipContent}>
                        <InformationCircleIcon className="h-4 w-4 text-gray-500 cursor-help" />
                    </Tooltip>
                    {loadingDris && <p className="text-xs text-gray-500 mt-1">...</p>}
                </div>
                <div>
                    <label htmlFor="initiativeFilter" className="block text-sm font-medium text-gray-700 mb-1">Initiative:</label>
                    <select id="initiativeFilter" value={actionItemInitiativeFilter} onChange={handleInitiativeFilterChange} disabled={loadingInitiativesForFilter} className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md">
                        <option value="">All</option>
                        {initiativeListForFilter.map(name => (<option key={name} value={name}>{name}</option>))}
                    </select>
                    {loadingInitiativesForFilter && <p className="text-xs text-gray-500 mt-1">...</p>}
                </div>
            </div>

            <DetailedListView
                title="Pending Action Items"
                items={pendingActionItemsList}
                columns={actionItemColumns}
                isLoading={loadingActionItems}
                error={errorActionItems}
                entityType="action-items"
                clientId={clientId}
                neo4jService={neo4jService}
                onItemUpdate={handleActionItemUpdate}
                editableColumnsConfig={actionItemEditableColumnsConfig}
            />
        </>
    );
};

export default ActionItemListView;